import classNames from "classnames";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";

import Modal from "../Modal";
import CCBillCardForm from "@components/CCBillCardForm";

import usePostalCodes from "@hooks/usePostalCodes";
import { getPublicIP } from "@helpers/users";
import { useService } from "@/base/hooks/useService";
import { PaymentsService } from "@services/PaymentsService";
import { LocationsListsService } from "@services/LocationsListsService";
import { addCardErrors } from "@const/errors";
import { getCards } from "@redux/paymentCards/actions";
import { initialValues as emptyInitialValues, validationSchema } from "./form";
import { ToasterService } from "@services/ToastService";
import {
  CCBILL_IFRAME_ID,
  paymentGatewayTypes,
  THREE_DS_PASSED_STATUS,
} from "@const/payments";
import { encryptCardNumber } from "@helpers/encrypt";
import { appendCCBillIframe, formatThreeDSResponse } from "@helpers/payments";

import routes from "../../const/routes";

import styles from "./index.module.scss";

const AddCardCCBill = ({ isOpen, toggle, isAdding, isModal }) => {
  const { t } = useTranslation("errors");
  const { t: t1 } = useTranslation("modals");
  const { t: t2 } = useTranslation("buttons");
  const { t: t3 } = useTranslation("payment-methods");
  const { t: t4 } = useTranslation("labels");
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { email } = useSelector(({ profileReducer }) => profileReducer);
  const initialValues = {
    ...emptyInitialValues,
    email,
  };
  const postalCodes = usePostalCodes();

  const onSettings = pathname.includes(routes.SETTINGS());

  const [isLoading, setIsLoading] = useState(false);
  const [stateSelectEmpty, setStateSelectEmpty] = useState(false);
  const paymentsService = useService(PaymentsService);
  const toastService = useService(ToasterService);
  const locationsListsService = useService(LocationsListsService);

  const [countries, setCountries] = useState([]);
  const getCountries = async () => {
    try {
      setIsLoading(true);
      const { data } = await locationsListsService.getCountries();
      setCountries(data);
    } finally {
      setIsLoading(false);
    }
  };

  const formRef = useRef(null);

  useEffect(() => {
    getCountries();
  }, []);

  const handlePublicApi = async () => {
    const { ip } = await getPublicIP();
    await formik.setFieldValue("ipAddress", ip);
  };

  useEffect(() => {
    if (isOpen) {
      handlePublicApi();
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    setIsLoading(true);
    const widget = new window.ccbill.CCBillAdvancedWidget(
      import.meta.env.VITE_APP_CCBILL_KEY,
    );
    try {
      const { data } = await paymentsService.createPaymentToken();

      const result = await widget.createPaymentToken(
        data.token,
        import.meta.env.VITE_APP_CCBILL_ACCOUNT_NUM,
        import.meta.env.VITE_APP_CCBILL_SUB_ACCOUNT_NUM,
      );

      const parsedResponse = await result.json();
      const paymentTokenId = parsedResponse.paymentTokenId;
      const firstFourNumbers = formik.values.cardNumber
        .replace(/ /g, "")
        .slice(0, 4);
      const lastFourNumbers = formik.values.cardNumber
        .replace(/ /g, "")
        .slice(-4);

      const cardNumber = await encryptCardNumber(
        formik.values.cardNumber.replace(/ /g, ""),
      );
      const { data: cardData } = await paymentsService.postCardToken({
        cardToken: paymentTokenId,
        firstFourNumbers,
        lastFourNumbers,
        cardNumber,
        expMonth: formik.values.expirationMonth,
        expYear: formik.values.expirationYear,
        email: formik.values.email,
        paymentGatewayType: paymentGatewayTypes.ccbill,
      });

      if (cardData?.is3DSRequired) {
        const resultAuth = await widget.authenticateCustomer(
          data.token,
          import.meta.env.VITE_APP_CCBILL_ACCOUNT_NUM,
          import.meta.env.VITE_APP_CCBILL_SUB_ACCOUNT_NUM,
          formRef.current,
          CCBILL_IFRAME_ID,
          paymentTokenId,
        );
        const threeDSecure = formatThreeDSResponse(resultAuth, paymentTokenId);

        if (resultAuth.status === THREE_DS_PASSED_STATUS) {
          const cardNumber = await encryptCardNumber(
            formik.values.cardNumber.replace(/ /g, ""),
          );
          await paymentsService.postCardToken({
            cardToken: paymentTokenId,
            firstFourNumbers,
            lastFourNumbers,
            cardNumber,
            expMonth: formik.values.expirationMonth,
            expYear: formik.values.expirationYear,
            email: formik.values.email,
            threeDSecure,
            paymentGatewayType: paymentGatewayTypes.ccbill,
          });
          await formik.setValues(initialValues);
        } else {
          throw Error(resultAuth.transStatusReasonDetail);
        }
      }
      toastService.success(t3("SUCCESSFULLY_ADDED_CARD"));
      dispatch(getCards());
      formik.resetForm({ values: initialValues });
      toggle(false);
    } catch (error) {
      if (!!error?.clone) {
        const jsonError = await error?.clone()?.json();
        const errorCode = jsonError.errorCode;
        const errorMessage = addCardErrors[errorCode]
          ? t(addCardErrors[errorCode])
          : jsonError.generalMessage;
        if (errorMessage) {
          toastService.error(errorMessage);
          return;
        }
      }

      const errors = [];
      if (error?.length) {
        error.forEach(function (item) {
          const msg = item.message.split(".");
          errors.push(msg[1]);
        });
        errors.forEach(function (item) {
          toastService.error(item);
        });
      } else {
        toastService.error(error.generalMessage || error.message);
      }
    } finally {
      setIsLoading(false);
      appendCCBillIframe();
    }
  };

  const formik = useFormik({
    onSubmit: handleSubmit,
    initialValues,
    validationSchema: validationSchema(!stateSelectEmpty, postalCodes, t, t4),
  });

  useEffect(() => {
    void formik.validateForm();
  }, [stateSelectEmpty]);

  const submitDisabled =
    isAdding || isLoading || !(formik.isValid && formik.dirty);

  const Container = isModal ? Modal : React.Fragment;

  return (
    <Container
      isOpen={isOpen}
      toggle={toggle}
      onClose={toggle}
      onCancel={toggle}
      size="md-3"
      title={t1("ADD_CARD")}
      confirmText={t2("ADD")}
      cancelText={t2("CANCEL")}
      submitDisabled={submitDisabled}
      cancelDisabled={isLoading}
      onConfirm={handleSubmit}
      dialogClassName="modal-fullscreen-xl-down"
      bodyClassName={styles.modalBody}
      footerClassName={classNames(styles.modalFooter)}
      confirmClassName={classNames(
        styles.modalButton,
        "ms-3",
        " btn-width-sm-2-xl",
      )}
      cancelClassName={classNames(styles.modalButton, "btn-width-sm-2-xl")}
    >
      <CCBillCardForm
        formRef={formRef}
        isOpen={isOpen}
        isLoading={isLoading}
        formik={formik}
        onSettings={onSettings}
        countries={countries}
        setStateSelectEmpty={setStateSelectEmpty}
        id="addCardModalId"
      />
    </Container>
  );
};

export default AddCardCCBill;
